import { useCallback, useEffect, useState } from 'react';
import { navPrefs } from '@/api/endpoints';

/**
 * Kolejność menu głównego ustawiana przez użytkownika.
 *
 * Model: płaska lista identyfikatorów sekcji i pozycji — ta sama postać co NAV w Layout.tsx.
 * Pozycja należy do sekcji, której nagłówek stoi nad nią, więc przeniesienie linku za inny
 * nagłówek przenosi go do innej sekcji, a przeniesienie sekcji przenosi cały jej blok.
 * Uzgodnienie z NAV robi {@link reconcileOrder}: nowe pozycje wskakują obok poprzednika
 * z NAV, usunięte znikają — zapis nigdy nie „gubi" pozycji dodanej po jego utworzeniu.
 */
export interface NavEntry { kind: 'section' | 'link'; id: string }

export type DropPlace = 'before' | 'after';

/** Uzgadnia zapisaną kolejność z aktualnym NAV. Brak zapisu → NAV bez zmian. */
export function reconcileOrder<T extends NavEntry>(nav: T[], saved: string[] | null | undefined): T[] {
  if (!saved || saved.length === 0) return [...nav];
  const byId = new Map(nav.map(it => [it.id, it]));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const id of saved) {
    const it = byId.get(id);
    if (!it || seen.has(id)) continue;
    seen.add(id);
    out.push(it);
  }
  // Brakujące (nowe w NAV) — za ich poprzednikiem z NAV, który w tym momencie już jest w `out`.
  nav.forEach((it, i) => {
    if (seen.has(it.id)) return;
    const at = i === 0 ? 0 : out.findIndex(x => x.id === nav[i - 1].id) + 1;
    out.splice(at, 0, it);
    seen.add(it.id);
  });
  // Link przed pierwszym nagłówkiem nie miałby sekcji (renderer go pomija) — dociągamy nagłówek.
  if (out.length > 0 && out[0].kind !== 'section') {
    const s = out.findIndex(x => x.kind === 'section');
    if (s > 0) out.unshift(...out.splice(s, 1));
  }
  return out;
}

/** Zakres [start, end) bloku: sekcja = nagłówek + jej linki, link = on sam. */
function blockOf(order: NavEntry[], idx: number): [number, number] {
  if (order[idx].kind === 'link') return [idx, idx + 1];
  let end = idx + 1;
  while (end < order.length && order[end].kind !== 'section') end++;
  return [idx, end];
}

/** Indeks nagłówka sekcji, do której należy pozycja `idx`. */
function ownerSection(order: NavEntry[], idx: number): number {
  for (let i = idx; i >= 0; i--) if (order[i].kind === 'section') return i;
  return 0;
}

/**
 * Przenosi pozycję `dragId` przed/za `targetId`.
 *  - link na link: przed/za nim (także w innej sekcji),
 *  - link na nagłówek: „za" = na początek tej sekcji, „przed" = na koniec poprzedniej,
 *  - sekcja na cokolwiek: cały blok przed/za blokiem sekcji, do której należy cel.
 * Zwraca nową tablicę (albo tę samą, gdy ruch jest pusty).
 */
export function moveEntry<T extends NavEntry>(order: T[], dragId: string, targetId: string, place: DropPlace): T[] {
  if (dragId === targetId) return order;
  const from = order.findIndex(x => x.id === dragId);
  const to = order.findIndex(x => x.id === targetId);
  if (from < 0 || to < 0) return order;

  const [bs, be] = blockOf(order, from);
  if (to >= bs && to < be) return order; // sekcja na własną pozycję
  const moved = order.slice(bs, be);
  const rest = [...order.slice(0, bs), ...order.slice(be)];
  const t = rest.findIndex(x => x.id === targetId);

  let at: number;
  if (order[from].kind === 'section') {
    const [ts, te] = blockOf(rest, ownerSection(rest, t));
    at = place === 'before' ? ts : te;
  } else if (rest[t].kind === 'section') {
    // Przed pierwszym nagłówkiem nie ma sekcji — wtedy na początek tej pierwszej.
    at = place === 'after' || t === 0 ? t + 1 : t;
  } else {
    at = place === 'before' ? t : t + 1;
  }
  const out = [...rest.slice(0, at), ...moved, ...rest.slice(at)];
  return out.every((x, i) => x === order[i]) ? order : out;
}

/**
 * Krok w górę/dół wśród WIDOCZNYCH wierszy (strzałki — dla dotyku i klawiatury, gdzie
 * przeciąganie HTML5 nie działa). Pozycje ukryte uprawnieniami są przeskakiwane.
 */
export function stepEntry<T extends NavEntry>(order: T[], id: string, dir: -1 | 1, isVisible: (id: string) => boolean): T[] {
  const idx = order.findIndex(x => x.id === id);
  if (idx < 0) return order;
  const self = order[idx];
  if (self.kind === 'section') {
    // Sąsiednia widoczna sekcja.
    for (let i = idx + dir; i >= 0 && i < order.length; i += dir) {
      if (order[i].kind === 'section' && isVisible(order[i].id)) {
        return moveEntry(order, id, order[i].id, dir < 0 ? 'before' : 'after');
      }
    }
    return order;
  }
  for (let i = idx + dir; i >= 0 && i < order.length; i += dir) {
    const o = order[i];
    if (!isVisible(o.id)) continue;
    if (o.kind === 'link') return moveEntry(order, id, o.id, dir < 0 ? 'before' : 'after');
    // Nagłówek: w górę = koniec poprzedniej widocznej sekcji, w dół = początek tej sekcji.
    if (dir > 0) return moveEntry(order, id, o.id, 'after');
    for (let j = i - 1; j >= 0; j--) {
      if (order[j].kind === 'section' && isVisible(order[j].id)) {
        const [, end] = blockOf(order, j);
        // Za ostatnią pozycją poprzedniej sekcji (albo za jej nagłówkiem, gdy pusta).
        return moveEntry(order, id, order[end - 1].id, 'after');
      }
    }
    return order;
  }
  return order;
}

const cacheKey = (userId: number | undefined) => `hw_nav_order_v1:${userId ?? 'anon'}`;

function readCache(userId: number | undefined): string[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    const v = raw ? JSON.parse(raw) : null;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;
  } catch { return null; }
}

function writeCache(userId: number | undefined, order: string[] | null) {
  try {
    if (order && order.length) localStorage.setItem(cacheKey(userId), JSON.stringify(order));
    else localStorage.removeItem(cacheKey(userId));
  } catch { /* localStorage może być wyłączone */ }
}

/**
 * Zapisana kolejność menu: źródłem prawdy jest konto (serwer), a kopia w localStorage
 * pozwala narysować menu we właściwej kolejności od pierwszej klatki, zanim przyjdzie odpowiedź.
 */
export function useNavOrder(userId: number | undefined) {
  const [saved, setSaved] = useState<string[] | null>(() => readCache(userId));

  useEffect(() => {
    setSaved(readCache(userId));
    if (userId == null) return;
    let cancelled = false;
    navPrefs.get()
      .then(res => {
        if (cancelled) return;
        const order = res?.order ?? null;
        setSaved(order);
        writeCache(userId, order);
      })
      .catch(() => { /* niedostępne → zostaje kopia lokalna / domyślna */ });
    return () => { cancelled = true; };
  }, [userId]);

  /** Utrwala kolejność; null = kolejność domyślna. */
  const save = useCallback(async (order: string[] | null) => {
    await navPrefs.put(order ?? []);
    setSaved(order);
    writeCache(userId, order);
  }, [userId]);

  return { saved, save };
}
